import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateDoc } from '../../docs/hooks/useDocsData';

// Drag & drop del Índice del Cuadro de Trabajo (integración main→dev, epic D1.3). Reordena y
// re-anida documentos. La persistencia va por `useUpdateDoc` de `features/docs` (`{parent_id,
// order_index}`) — NO un `Promise.allSettled` crudo de `docsApi` como el `main` original, NO un
// endpoint de tareas. La invalidación de `['docs','list']` que hace `useUpdateDoc` refresca el
// árbol (sin update optimista: el refetch llega en ~1 frame y evita divergencias de estado).

function isDescendant(docs, parentId, targetId) {
    if (!parentId || !targetId) return false;
    if (String(parentId) === String(targetId)) return true;
    let current = docs.find((d) => String(d.id) === String(targetId));
    while (current && current.parent_id) {
        if (String(current.parent_id) === String(parentId)) return true;
        current = docs.find((d) => String(d.id) === String(current.parent_id));
    }
    return false;
}

export function useWorkframeIndexDnD({ rawDocs, rootDocId, onReordered, onError }) {
    const updateDoc = useUpdateDoc();
    const queryClient = useQueryClient();
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [dropPosition, setDropPosition] = useState(null); // 'top' | 'bottom' | 'inside'

    const reset = useCallback(() => {
        setDraggingId(null);
        setDragOverId(null);
        setDropPosition(null);
    }, []);

    const handleDragStart = useCallback((e, node) => {
        e.stopPropagation();
        setDraggingId(node.id);
        e.dataTransfer.setData('text/plain', String(node.id));
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e, node) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggingId || String(draggingId) === String(node.id)) return;
        if (isDescendant(rawDocs, draggingId, node.id)) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const relY = e.clientY - rect.top;
        let pos = 'inside';
        if (String(node.id) === String(rootDocId)) {
            pos = 'inside';
        } else if (relY < rect.height * 0.25) {
            pos = 'top';
        } else if (relY > rect.height * 0.75) {
            pos = 'bottom';
        }
        if (dragOverId !== node.id || dropPosition !== pos) {
            setDragOverId(node.id);
            setDropPosition(pos);
        }
    }, [draggingId, rawDocs, rootDocId, dragOverId, dropPosition]);

    const handleDragLeave = useCallback((e, node) => {
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dragOverId === node.id) {
            setDragOverId(null);
            setDropPosition(null);
        }
    }, [dragOverId]);

    const handleDragEnd = useCallback((e) => {
        e.stopPropagation();
        reset();
    }, [reset]);

    const handleDrop = useCallback(async (e, targetNode) => {
        e.preventDefault();
        e.stopPropagation();
        const draggedIdStr = e.dataTransfer?.getData('text/plain') || draggingId;
        const currentDropPos = dropPosition;
        reset();

        if (!draggedIdStr || String(draggedIdStr) === String(targetNode.id)) return;
        if (String(draggedIdStr) === String(rootDocId)) return;
        if (isDescendant(rawDocs, draggedIdStr, targetNode.id)) return;

        const draggedDoc = rawDocs.find((d) => String(d.id) === String(draggedIdStr));
        if (!draggedDoc) return;

        const dropInside = currentDropPos === 'inside' || String(targetNode.id) === String(rootDocId);
        const newParentId = dropInside ? targetNode.id : targetNode.parent_id;

        const siblings = rawDocs
            .filter((d) => String(d.parent_id) === String(newParentId) && String(d.id) !== String(draggedIdStr))
            .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

        let newSiblings;
        if (dropInside) {
            newSiblings = [...siblings, draggedDoc];
        } else {
            const targetIdx = siblings.findIndex((s) => String(s.id) === String(targetNode.id));
            if (targetIdx === -1) {
                newSiblings = [...siblings, draggedDoc];
            } else if (currentDropPos === 'top') {
                newSiblings = [...siblings.slice(0, targetIdx), draggedDoc, ...siblings.slice(targetIdx)];
            } else {
                newSiblings = [...siblings.slice(0, targetIdx + 1), draggedDoc, ...siblings.slice(targetIdx + 1)];
            }
        }

        const orderMap = new Map();
        newSiblings.forEach((doc, idx) => orderMap.set(String(doc.id), (idx + 1) * 10));
        const newDraggedOrder = orderMap.get(String(draggedDoc.id)) || 10;

        // Optimistic UI Update
        queryClient.setQueriesData({ queryKey: ['docs', 'list'] }, (oldData) => {
            if (!oldData || !Array.isArray(oldData)) return oldData;
            return oldData.map((doc) => {
                const docIdStr = String(doc.id);
                if (docIdStr === String(draggedDoc.id)) {
                    return { ...doc, parent_id: newParentId, order_index: newDraggedOrder };
                }
                const newOrd = orderMap.get(docIdStr);
                if (newOrd !== undefined) {
                    return { ...doc, order_index: newOrd };
                }
                return doc;
            });
        });

        try {
            const writes = [
                updateDoc.mutateAsync({ id: draggedDoc.id, payload: { parent_id: newParentId, order_index: newDraggedOrder } }),
            ];
            newSiblings.forEach((doc) => {
                if (String(doc.id) === String(draggedDoc.id)) return;
                const newOrd = orderMap.get(String(doc.id));
                if (doc.order_index !== newOrd) {
                    writes.push(updateDoc.mutateAsync({ id: doc.id, payload: { order_index: newOrd } }));
                }
            });
            await Promise.all(writes);
            onReordered?.();
        } catch (err) {
            queryClient.invalidateQueries({ queryKey: ['docs', 'list'] });
            onError?.(err);
        }
    }, [draggingId, dropPosition, rawDocs, rootDocId, updateDoc, queryClient, reset, onReordered, onError]);

    return {
        draggingId,
        dragOverId,
        dropPosition,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDragEnd,
        handleDrop,
    };
}
